import { v4 as uuid } from 'uuid'
import { Quest, QuestPerms } from 'types/quests'
import { Answer } from 'types/answers'
import answerModel from 'components/answers/answerModel'
import useStore from 'state/knovStore'
import type { IAiModel } from 'state/aiModels'
import { getCachedQuest, cacheQuest } from 'state/cache'
import { withDepth, Filter } from 'components/filters/useStreamFilters'
import {
    canUserReplyInMainThread,
    needsChannelJoinToReply,
    canUserReplyToAnswer,
    canUserPostToTeam,
    needsChannelJoinToPost,
    isTeamAdmin,
    isTeamMember,
    replyTargetQuestForAnswer,
    teamPermissionRule,
    teamPermsFromFilterTeamId,
    type TeamLike,
} from 'components/quests/questPerms'
import { currentUserSnapshot } from 'lib/currentUserSnapshot'

type PermsFromFilterOptions = {
    team?: TeamLike | null
}

const model = {
    isPrivate: quest => quest.private && !quest.team_id,

    isPublic: quest => quest.public && !quest.private && !quest.team_id,

    isLinkShare: quest => !quest.public && !quest.private && !quest.team_id,

    hasTeam: quest => !!quest.team,

    isStreamThread: quest => !!quest?.team_id,

    usesOpenclawStreamRouting: (quest, aiModels: IAiModel[] = []) => {
        const teamId = String(quest?.team_id || '').trim()
        if (!teamId) return false

        return aiModels.some(model => {
            const provider = String(model?.provider || '').trim()
            const visibilityScope = String(model?.openclawVisibilityScope || '').trim()
            const teamIds = Array.isArray(model?.openclawTeamIds)
                ? model.openclawTeamIds.map(id => String(id))
                : []

            return (
                provider === 'openclaw' && visibilityScope === 'stream' && teamIds.includes(teamId)
            )
        })
    },

    isTeamAdmin,

    isTeamMember,

    teamPermissionRule,

    canUserPostToTeam,
    needsChannelJoinToPost,

    canUserReplyToTeam: (team, user) => {
        if (!team || !user) return false
        const rule = model.teamPermissionRule(team, team.who_can_reply_threads)
        if (rule === 'admin') return model.isTeamAdmin(team, user)
        if (rule === 'members') return model.isTeamMember(team, user)
        return true
    },

    // Thread-level reply policy: replies_enabled === false restricts this
    // quest's main thread to its author. Each child quest has its own policy.
    canUserReplyInMainThread,
    needsChannelJoinToReply,

    // A title answer posts into the quest currently being rendered. Any other
    // answer posts into that answer's child quest, whose policy may have been
    // changed independently after it was published.
    replyTargetQuestForAnswer: (answer, quest, isTitle) =>
        replyTargetQuestForAnswer(answer, quest, isTitle, childQuest =>
            getCachedQuest(childQuest.id),
        ),

    canUserReplyToAnswer: (answer, quest, isTitle, user) =>
        canUserReplyToAnswer(answer, quest, isTitle, user, childQuest =>
            getCachedQuest(childQuest.id),
        ),

    getFilter: quest => {
        if (model.isPrivate(quest)) {
            return withDepth({ private: true })
        } else if (model.isPublic(quest)) {
            return withDepth({ public: true })
        } else if (model.isLinkShare(quest)) {
            return withDepth({ link: true })
        } else if (model.hasTeam(quest)) {
            if (quest.team_id === gon.HODLOCKER_TEAM_ID) {
                return withDepth({ hodlocker: true })
            } else if (quest.team_id === gon.TWETCH_TEAM_ID) {
                return withDepth({ twetch: true })
            } else {
                return withDepth({ team_ids: [quest.team_id] })
            }
        }
    },

    // A New Music thread that is still public. Once its author moves it to
    // Private, a link or another channel, it shows that scope instead.
    isMusicChannelThread: quest =>
        quest?.thread_type === 'music' && Boolean(gon?.RADIO_TEAM_ID) && model.isPublic(quest),

    // The channel a thread shows in its breadcrumb. A music thread (New Music)
    // is public, but it belongs with the music channel it was posted from.
    channelFilter: quest => {
        if (model.isMusicChannelThread(quest)) {
            return withDepth({ team_ids: [gon.RADIO_TEAM_ID] })
        }
        return model.getFilter(quest)
    },

    sortedQuestAnswers: quest => {
        const col = quest.default_sort_col || 'position'
        const dir = quest.default_sort_dir || 'asc'
        let answers
        if (col === 'position' && dir === 'asc') {
            answers = model.positionAnswers(quest.sorted_answers)
        } else if (col === 'position' && dir === 'desc') {
            answers = model.positionAnswers(quest.sorted_answers).reverse()
        } else if (col === 'vote' && dir === 'desc') {
            answers = model.votedAnswers(quest.sorted_answers)
        } else {
            answers = model.votedAnswers(quest.sorted_answers).reverse()
        }
        return answers
    },

    positionAnswers: answers =>
        answers ? [...answers].sort((a, b) => (a?.position || 0) - (b?.position || 0)) : [],

    filterUnarchivedAnswers: answers => (answers ? answers.filter(ans => !ans?.archive) : []),

    votedAnswers: answers => {
        return answers
            ? [...answers].sort((a, b) => {
                  const av = a.vote_total
                  const bv = b.vote_total
                  const auv = a.bsv_upvalue_amt
                  const buv = b.bsv_upvalue_amt
                  if (auv != buv) return buv - auv
                  const order = bv - av
                  return order === 0 ? a.position - b.position : order
              })
            : []
    },

    matchingAnswers: quest => {
        return quest?.sorted_answer_ids && quest?.matching_answer_ids
            ? quest.sorted_answer_ids.filter(ans_id => quest.matching_answer_ids.includes(ans_id))
            : []
    },

    matchingMatchingAnswers: quest => {
        return model
            .matchingAnswers(quest)
            .sort(
                (a, b) =>
                    quest.matching_answer_ids.indexOf(a.id) -
                    quest.matching_answer_ids.indexOf(b.id),
            )
    },

    showUnarchivedAnswers: quest => model.filterUnarchivedAnswers(model.sortedQuestAnswers(quest)),

    threadType: quest => {
        let type
        if (quest.thread_type === 'chatgpt') {
            type = {
                name: 'Knov GPT',
                icon: null,
                desc: 'This is a Knov GPT chatbot thread.',
            }
        } else if (quest.default_sort_col === 'vote') {
            type = {
                name: 'Quest',
                icon: 'fa-sort-amount-desc',
                desc: 'This is a Quest thread with messages sorted by Upvalue.',
            }
        } else if (quest.blog) {
            type = {
                name: 'Post',
                icon: 'fa-file',
                desc: 'This is a Post whose messages are written by a single author.',
            }
        } else {
            type = {
                name: 'Thread',
                icon: 'fa-list-ul',
                desc: 'This is a Conversation thread with potentially multiple participants.',
            }
        }
        return type
    },

    getPerms: (quest: Quest): QuestPerms => {
        if ([gon.TWETCH_TEAM_ID, gon.HODLOCKER_TEAM_ID].includes(quest.team_id)) {
            return {
                space_id: quest.space_id,
                team_id: null,
                team: null,
                private: null,
                public: true,
            }
        } else {
            return {
                space_id: quest.space_id,
                team_id: quest.team_id,
                team: quest.team,
                private: quest.private,
                public: quest.public,
            }
        }
    },

    permsFromFilter: (filter: Filter, options: PermsFromFilterOptions = {}): QuestPerms => {
        let perms: QuestPerms = {
            team_id: null,
            team: null,
            private: null,
            public: null,
        }
        if (filter.questId) {
            const quest = getCachedQuest(filter.questId)
            perms = quest ? model.getPerms(quest) : null
        } else if (filter.private)
            // Only the Private channel starts private; every other non-channel
            // view starts public.
            perms.private = true
        // Set null if undefined so it's correctly stringified.
        else if (filter.team_ids?.length) {
            const teamId = filter.team_ids[0]
            const user = gon.currentUser
            const store = useStore.getState()

            // Make the new-thread perms match what the user can actually create.
            // If posting isn't allowed in the stream, default to private thread perms.
            // If team metadata is not hydrated yet, keep stream context and avoid
            // falsely showing private perms in the UI.
            perms = teamPermsFromFilterTeamId(teamId, user, {
                team: options.team,
                publicTeams: store?.publicTeams,
                userStreams: store?.userStreams,
            })
        } else {
            perms.public = true
        }

        return perms
    },

    hasLLMReply: quest => {
        return [quest.parent, ...model.showUnarchivedAnswers(quest)].some(a =>
            answerModel.hasLLMReply(a),
        )
    },

    newQuest: (quest: Partial<Quest>): Quest => {
        const questId = uuid()

        let localQuest: Partial<Quest> = {
            id: questId,
            is_draft: true,
            ...quest,
        }

        if (!localQuest.space_id) useStore.getState().getSpaceId()
        const now = new Date()
        if (!localQuest.created_at) localQuest.created_at = now
        if (!localQuest.updated_at) localQuest.updated_at = now
        if (!localQuest.path) localQuest.path = localQuest.is_draft ? '/new' : `/quest/${questId}`

        if (!localQuest.user_id) localQuest.user_id = gon.currentUser?.id
        if (!localQuest.user) localQuest.user = currentUserSnapshot() as Quest['user']

        if (!localQuest.parent_id) {
            localQuest.parent = answerModel.newAnswer({
                is_draft: true,
                user_id: localQuest.user_id,
                user: localQuest.user,
                child_quests: [
                    {
                        id: questId,
                    } as Quest,
                ],
            }) as Quest['parent']
            localQuest.parent_id = localQuest.parent.id
        }

        if (!localQuest.sorted_answers) localQuest.sorted_answers = []
        return localQuest as Quest
    },

    addDraftAnswer: (quest: Quest & { draft?: Answer }, draftAnswer: Answer) => {
        quest.draft = draftAnswer
    },

    newQuestFromFilter: (
        quest: Partial<Quest>,
        filter: Filter,
        options: PermsFromFilterOptions = {},
    ): Quest => {
        const perms = model.permsFromFilter(filter, options)

        let newQuest: Quest = model.newQuest({
            ...quest,
            ...perms,
        })

        return newQuest
    },

    getParentQuestId: (quest: Quest): string => {
        return quest?.parent?.quest_id
    },

    getChildQuestIds: (quest: Quest): string[] => {
        return quest?.sorted_answer_ids
            ?.map(aid => getCachedAnswer(aid))
            ?.map(a => a?.user_id !== gon.SYSTEM_USER && a?.child_quests?.[0]?.id)
            .filter(id => id)
    },

    getChildQuests: async (
        questId: string,
        opts: { uncached?: boolean; includeTrunk?: boolean; includeParent?: boolean } = {},
    ) => {
        const BATCH_SIZE = 20
        const quest = getCachedQuest(questId) as Quest
        let filteredQuestIds = model.getChildQuestIds(quest)
        if (opts.includeTrunk) filteredQuestIds.push(questId)
        if (opts.includeParent) filteredQuestIds.push(model.getParentQuestId(quest))

        if (opts.uncached)
            filteredQuestIds = filteredQuestIds.filter(id => !!id && !getCachedQuest(id))

        if (filteredQuestIds?.length > 0) {
            let quests = []

            await Promise.all(
                filteredQuestIds.map(async (_, i) => {
                    if (i % BATCH_SIZE === 0) {
                        const batchIds = filteredQuestIds.slice(i, i + BATCH_SIZE)
                        api.getQuests({ ids: batchIds }).then(apiQuests => {
                            if (apiQuests?.length > 0) {
                                apiQuests.forEach(quest => {
                                    cacheQuest(quest)
                                    quests = quests.concat(apiQuests)
                                })
                            }
                            return apiQuests
                        })
                    }
                }),
            )

            return quests
        } else return []
    },
}

export default model
